import React from "react";
import "./App.css";
import "./css/destyle.css"
import styled from "styled-components"

import {
  createClient, Provider, cacheExchange, fetchExchange
} from "urql";
import { ShiftPage } from "./pages/ShiftPage";
import { HomePage } from "./pages/HomePage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { createConfig } from "./Config";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { LoginPage } from "./pages/LoginPage";
import { WorkplaceSelectPage } from "./pages/WorkplaceSelectPage";
import { AppShell } from "./components/AppShell";
import { logPageView } from "./firebase";

const config = createConfig()

const AppContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #f4f5f7;
`;

type ContainerProps = {
  children: React.ReactNode;
};
const Container: React.FC<ContainerProps> = (props) => {
  return <AppContainer>{props.children}</AppContainer>;
};

const createBasicAuthHeader = () => {
  if (!config.basicAuthUsername || !config.basicAuthPassword) {
    return null;
  }

  return `Basic ${btoa(`${config.basicAuthUsername}:${config.basicAuthPassword}`)}`;
};

const defaultDocumentTitle = "Shiftas - シフト管理をもっと簡単に";

const getDocumentTitle = (pathname: string) => {
  if (pathname === "/") {
    return defaultDocumentTitle;
  }

  if (pathname === "/login") {
    return "ログイン | Shiftas";
  }

  if (pathname === "/workplaces") {
    return "職場を選択 | Shiftas";
  }

  if (/^\/workplaces\/[^/]+\/shifts$/.test(pathname)) {
    return "シフト表 | Shiftas";
  }

  return "ページが見つかりません | Shiftas";
};

const ShiftRouteShell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const auth = useAuth();

  // シフト表の直URLは従来通り未ログインでも開ける。
  // ログイン済みの場合だけ職場切り替えナビを重ねる。
  if (auth.state === "authenticated") {
    return <AppShell>{children}</AppShell>;
  }

  return <>{children}</>;
};

function AuthenticatedApp() {
  const auth = useAuth();
  const location = useLocation();

  React.useEffect(() => {
    document.title = getDocumentTitle(location.pathname);
    void logPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  const client = React.useMemo(
    () =>
      createClient({
        url: config.apiUrl + "/admin/graphql",
        exchanges: [cacheExchange, fetchExchange],
        fetch: async (input, init) => {
          const token = await auth.getIdToken();
          const headers = new Headers(init?.headers);
          headers.set("Content-Type", "application/json");
          if (token) {
            headers.set("Authorization", `Bearer ${token}`);
          } else {
            const basicAuthHeader = createBasicAuthHeader();
            if (basicAuthHeader) {
              headers.set("Authorization", basicAuthHeader);
            }
          }
          return fetch(input, { ...init, method: "POST", headers });
        },
        preferGetMethod: false,
      }),
    [auth]
  );

  return (
    <Provider value={client}>
      <Container>
        <Routes>
          <Route
            path="/"
            element={
              auth.state === "authenticated" ? (
                <Navigate to="/workplaces" replace />
              ) : (
                <HomePage />
              )
            }
          />
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/workplaces"
            element={
              <ProtectedRoute>
                <AppShell>
                  <WorkplaceSelectPage />
                </AppShell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/workplaces/:workplaceId/shifts"
            element={
              <ShiftRouteShell>
                <ShiftPage />
              </ShiftRouteShell>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Container>
    </Provider>
  );
}

function App(props: any) {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}

export default App;
